import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Newspaper, Plane, Baby, Music, Film, Crown, Award } from 'lucide-react';
import { toast } from 'sonner';

const Entertainment = () => {
  const navigate = useNavigate();
  const [showSubscription, setShowSubscription] = useState(false);

  const channels = [
    {
      id: 'news',
      name: 'News',
      description: 'Global news coverage 24/7',
      icon: Newspaper,
      color: '#EF4444',
      feeds: [
        { name: 'BBC News', url: 'https://www.youtube.com/@BBCNews/live', thumbnail: 'https://images.unsplash.com/photo-1504711434969-e33886168f5c' },
        { name: 'Al Jazeera', url: 'https://www.youtube.com/@aljazeeraenglish/live', thumbnail: 'https://images.unsplash.com/photo-1495020689067-958852a7765e' },
        { name: 'Sky News', url: 'https://www.youtube.com/@SkyNews/live', thumbnail: 'https://images.unsplash.com/photo-1585829365295-ab7cd400c167' }
      ]
    },
    {
      id: 'travel',
      name: 'Travel',
      description: 'Explore destinations worldwide',
      icon: Plane,
      color: '#FFD60A',
      feeds: [
        { name: 'Travel Channel', url: 'https://www.youtube.com/@TravelChannelUK', thumbnail: 'https://images.unsplash.com/photo-1488646953014-85cb44e25828' },
        { name: 'Rick Steves', url: 'https://www.youtube.com/@ricksteves', thumbnail: 'https://images.unsplash.com/photo-1469854523086-cc02fe5d8800' },
        { name: 'Discover Nigeria', url: 'https://www.youtube.com/results?search_query=nigeria+travel+documentary', thumbnail: 'https://images.unsplash.com/photo-1696339058184-2e6d1da36ad3' }
      ]
    },
    {
      id: 'children',
      name: 'Children',
      description: 'Educational & fun content for kids',
      icon: Baby,
      color: '#10B981',
      feeds: [
        { name: 'Sesame Street', url: 'https://www.youtube.com/@sesamestreet', thumbnail: 'https://images.unsplash.com/photo-1503454537195-1dcabb73ffb9' },
        { name: 'PBS Kids', url: 'https://www.youtube.com/@PBSKIDS', thumbnail: 'https://images.unsplash.com/photo-1587654780291-39c9404d746b' },
        { name: 'National Geographic Kids', url: 'https://www.youtube.com/@NatGeoKids', thumbnail: 'https://images.unsplash.com/photo-1472457897821-70d3819a0e24' }
      ]
    },
    {
      id: 'music',
      name: 'Music',
      description: 'Music videos & live performances',
      icon: Music,
      color: '#8B5CF6',
      feeds: [
        { name: 'Vevo', url: 'https://www.youtube.com/@vevo', thumbnail: 'https://images.unsplash.com/photo-1511671782779-c97d3d27a1d4' },
        { name: 'Afrobeats', url: 'https://www.youtube.com/results?search_query=afrobeats+2026', thumbnail: 'https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f' },
        { name: 'Live Concerts', url: 'https://www.youtube.com/results?search_query=live+concert+2026', thumbnail: 'https://images.unsplash.com/photo-1540039155733-5bb30b53aa14' }
      ]
    },
    {
      id: 'movies',
      name: 'Movies',
      description: 'Classic films & new releases',
      icon: Film,
      color: '#F59E0B',
      feeds: [
        { name: 'Classic Movies', url: 'https://www.youtube.com/results?search_query=classic+movies+full', thumbnail: 'https://images.unsplash.com/photo-1489599849927-2ee91cede3ba' },
        { name: 'Nollywood', url: 'https://www.youtube.com/results?search_query=nollywood+movies+2026', thumbnail: 'https://images.unsplash.com/photo-1594908900066-3f47337549d8' },
        { name: 'Movie Trailers', url: 'https://www.youtube.com/@MoviesTrailers', thumbnail: 'https://images.unsplash.com/photo-1536440136628-849c177e76a1' }
      ]
    }
  ];

  const subscriptionTiers = [
    {
      name: 'Free',
      price: 0,
      points: 0,
      features: ['Access to all 5 channels', 'YouTube-based streaming', 'Standard quality', 'Ads included']
    },
    {
      name: 'Premium',
      price: 9.99,
      points: 100,
      features: ['Ad-free experience', 'HD quality streaming', 'Earn 100 loyalty points/month', 'Offline downloads', 'Priority support'],
      popular: true
    },
    {
      name: 'Family',
      price: 14.99,
      points: 200,
      features: ['Up to 5 devices', 'All Premium features', 'Earn 200 loyalty points/month', 'Kids content library', 'Exclusive content']
    }
  ];

  return (
    <div className="min-h-screen bg-[#09090B] p-4 md:p-8" data-testid="entertainment-page">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-white/60 hover:text-white mb-4 transition-colors"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
            <span style={{ fontFamily: 'Manrope' }}>Back to Home</span>
          </button>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-2" style={{ fontFamily: 'Sora' }}>Entertainment</h1>
              <p className="text-white/60" style={{ fontFamily: 'Manrope' }}>Free streaming channels - No registration required</p>
            </div>
            <button
              onClick={() => setShowSubscription(!showSubscription)}
              className="btn-primary flex items-center gap-2"
              data-testid="subscription-btn"
            >
              <Crown className="w-5 h-5" />
              View Plans
            </button>
          </div>
        </div>

        {/* Subscription Tiers */}
        {showSubscription && (
          <div className="mb-12 animate-in slide-in-from-top duration-500">
            <h2 className="text-3xl font-bold text-white mb-6" style={{ fontFamily: 'Sora' }}>Subscription Plans</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {subscriptionTiers.map((tier) => (
                <div
                  key={tier.name}
                  className={`card-glass p-6 md:p-8 relative ${
                    tier.popular ? 'border-2 border-[#FFD60A]' : ''
                  }`}
                  data-testid={`tier-${tier.name.toLowerCase()}`}
                >
                  {tier.popular && (
                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-[#FFD60A] text-black px-4 py-1 text-xs font-bold uppercase">
                      Most Popular
                    </div>
                  )}
                  <h3 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: 'Sora' }}>{tier.name}</h3>
                  <div className="mb-4">
                    <span className="text-4xl font-bold text-[#FFD60A]" style={{ fontFamily: 'Sora' }}>${tier.price}</span>
                    <span className="text-white/60 text-sm">/month</span>
                  </div>
                  {tier.points > 0 && (
                    <div className="flex items-center gap-2 mb-4 bg-[#FFD60A]/10 border border-[#FFD60A]/30 px-3 py-2">
                      <Award className="w-4 h-4 text-[#FFD60A]" />
                      <span className="text-[#FFD60A] text-sm font-semibold">+{tier.points} points/month</span>
                    </div>
                  )}
                  <ul className="space-y-3 mb-6">
                    {tier.features.map((feature, index) => (
                      <li key={index} className="flex items-start gap-2 text-white/80 text-sm">
                        <span className="text-[#10B981] mt-1">✓</span>
                        <span style={{ fontFamily: 'Manrope' }}>{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <button
                    className={tier.price === 0 ? 'border border-white/20 text-white hover:bg-white/10 w-full py-3 transition-all' : 'btn-primary w-full'}
                    onClick={() => toast.info('Subscription coming soon!')}
                  >
                    {tier.price === 0 ? 'Current Plan' : 'Subscribe'}
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Channel Grid */}
        <div className="space-y-12">
          {channels.map((channel) => {
            const Icon = channel.icon;
            return (
              <div key={channel.id} data-testid={`channel-${channel.id}`}>
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-3 bg-white/5 border border-white/10">
                    <Icon className="w-6 h-6" style={{ color: channel.color }} strokeWidth={1.5} />
                  </div>
                  <div>
                    <h2 className="text-2xl md:text-3xl font-bold text-white" style={{ fontFamily: 'Sora' }}>{channel.name}</h2>
                    <p className="text-white/60 text-sm" style={{ fontFamily: 'Manrope' }}>{channel.description}</p>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
                  {channel.feeds.map((feed, index) => (
                    <a
                      key={index}
                      href={feed.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="card-glass overflow-hidden hover:border-white/30 transition-all duration-300 group"
                      data-testid={`feed-${channel.id}-${index}`}
                    >
                      <div className="relative h-48 overflow-hidden">
                        <img
                          src={feed.thumbnail}
                          alt={feed.name}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent"></div>
                        <div className="absolute bottom-4 left-4 right-4">
                          <h3 className="text-lg font-bold text-white" style={{ fontFamily: 'Sora' }}>{feed.name}</h3>
                        </div>
                        {/* Play Icon Overlay */}
                        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                          <div className="w-16 h-16 bg-[#FFD60A] rounded-full flex items-center justify-center">
                            <svg className="w-8 h-8 text-black ml-1" fill="currentColor" viewBox="0 0 20 20">
                              <path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z" />
                            </svg>
                          </div>
                        </div>
                      </div>
                    </a>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default Entertainment;